import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BikePurchaseRecurringClaimDTO,
  BalanceHistoryDTO,
  ClaimAuditLogDTO,
  ClaimDTO,
  ClaimDetailDTO,
  ClaimDocumentsDTO,
  ClaimHistoryDTO,
  ClaimSettingsDTO,
  CreateClaimDTO,
  CreateRecurringClaimDTO,
  CrmIssueContractDTO,
  CrmIssueDTO,
  CrmLostPassDTO,
  CrmStatusDTO,
  IssueDocumentDTO,
  MemberBBAdjustmentDTO,
  PreTaxCapDTO,
  CommuteDayDTO,
  RecordCommuteDayDTO,
  TPACompanyCRMSettingDTO,
  crmGrhClaimReasonDTO,
  documentResponse,
  selectedClaimDetails,
} from '../models/claim';
import { OrderProductInfo } from '../models/order';
import { AppConfigService } from './shared-services/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class ClaimService {
  apiName = 'claimService';
  claims$!: Observable<MemberBBAdjustmentDTO>;
  private claim: ClaimDTO;
  private orderDetails: OrderProductInfo;
  private claimSettings: ClaimSettingsDTO;
  public selectedClaimDetails: selectedClaimDetails;

  public set setOrderDetails(item: OrderProductInfo) {
    this.orderDetails = item;
  }
  public get getOrderDetails() {
    return this.orderDetails;
  }
  constructor(private http: HttpClient) {}
  /**
   * @return Success
   */
  getClaimHistoryByMember(idMember: number, recordCount: number): Observable<ClaimHistoryDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/claim-history/{IdMember}';
    url = url.replace('{IdMember}', idMember.toString());
    if (recordCount) {
      url += '?recordCount=' + recordCount;
    }
    return this.http.get<ClaimHistoryDTO>(url);
  }
  /**
   * @return Success
   */
  getClaimBBAdjustment(idMember: number): Observable<MemberBBAdjustmentDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/bb-adjustment-balance/{IdMember}';
    url = url.replace('{IdMember}', idMember.toString());
    return this.http.get<MemberBBAdjustmentDTO>(url);
  }
  /**
   * @return Success
   */
  getClaimDetail(idClaim: number): Observable<ClaimDetailDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/claim-details/{idClaim}';
    url = url.replace('{idClaim}', idClaim ? idClaim.toString() : '');
    return this.http.get<ClaimDetailDTO>(url);
  }
  getClaimAuditLog(idClaim: number, claimType: number): Observable<ClaimAuditLogDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/claim/auditlog/{idClaim}/{claimType}';
    url = url.replace('{idClaim}', idClaim.toString());
    url = url.replace('{claimType}', claimType.toString());
    return this.http.get<ClaimAuditLogDTO>(url);
  }
  getIssueDocument(idIssue: number): Observable<ClaimDocumentsDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/issue/{idIssue}/documents';
    url = url.replace('{idClaim}', idIssue.toString());
    return this.http.get<ClaimDocumentsDTO>(url);
  }
  getDocumentContent(idContent: string, sourceType: string): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/documents/{sourceType}/{idContent}';
    url = url.replace('{idContent}', idContent?.toString());
    url = url.replace('{sourceType}', sourceType?.toString());
    return this.http.get<ClaimDocumentsDTO>(url, { responseType: 'blob' as 'json' });
  }

  saveNewReceipt(documentDetails: IssueDocumentDTO): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/issue/document';
    return this.http.post<any>(url, documentDetails);
  }
  /**
   * @return Success
   */
  getClaimSettings(idMember: number, claimTypeId: number): Observable<ClaimSettingsDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/claim-settings?idMember=' + idMember.toString();
    if (claimTypeId) {
      url = url + '&claimTypeId=' + claimTypeId.toString();
    }
    return this.http.get<ClaimSettingsDTO>(url);
  }

  getClaimSettingsByCompany(idCompany: number, claimTypeId: number): Observable<ClaimSettingsDTO> {
    let url =
      AppConfigService.apiURL(this.apiName) + '/Claim/claim-settings-by-Company?idCompany=' + idCompany.toString();
    if (claimTypeId) {
      url = url + '&claimTypeId=' + claimTypeId.toString();
    }
    return this.http.get<ClaimSettingsDTO>(url);
  }

  addClaim(claim: CreateClaimDTO): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/claim/claim';
    return this.http.post<any>(url, claim);
  }

  addBikePurchaseClaim(idMember: number, claim: CreateRecurringClaimDTO): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{Idmember}';
    url = url.replace('{Idmember}', idMember.toString());
    return this.http.post<any>(url, claim);
  }

  SaveAndReadReceipt(formData: FormData, idCompanyClaimExpenseType: number = null): Observable<any> {
    const expenseTypeId = idCompanyClaimExpenseType ?? 0;

    let url = AppConfigService.apiURL(this.apiName) + '/Claim/Receipt';
      url += '?idExpenseType=' + expenseTypeId.toString();
    return this.http.post<any>(url, formData);
  }

  TransferBBBalance(formData: MemberBBAdjustmentDTO): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/Claim/TransferBBBalance';
    return this.http.post<any>(url, formData);
  }

  public set setClaimItem(claim: ClaimDTO) {
    this.claim = claim;
  }

  public get getClaimItem(): ClaimDTO {
    return this.claim;
  }

  public set setClaimSetting(claimSettings: ClaimSettingsDTO) {
    this.claimSettings = claimSettings;
  }

  public get getClaimSetting(): ClaimSettingsDTO {
    return this.claimSettings;
  }

  public set setSelectedClaimInfo(selectedClaim: selectedClaimDetails) {
    this.selectedClaimDetails = selectedClaim;
  }
  public get getSelectedClaimDetails(): selectedClaimDetails {
    return this.selectedClaimDetails;
  }

  getLostPass(idOrderProducts: number): Observable<CrmLostPassDTO[]> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/getlostpass';
    if (idOrderProducts) {
      url_ = url_ + '?idOrderProducts=' + idOrderProducts;
    }
    return this.http.get<CrmLostPassDTO[]>(url_);
  }

  addLostPass(CRMIssue: CrmIssueDTO): Observable<number> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/addlostpass';
    return this.http.post<number>(url_, CRMIssue);
  }

  uploadDocuments(formData: FormData): Observable<documentResponse> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/documents/upload';
    return this.http.post<documentResponse>(url_, formData);
  }
  isLostPassExist(idOrder: number, idOrderProducts: number, idIssueType: number): Observable<boolean> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/isissueexist';
    if (idOrder) {
      url_ = url_ + '?idOrder=' + idOrder;
    }
    if (idOrderProducts) {
      url_ = url_ + '&idOrderProducts=' + idOrderProducts;
    }
    if (idIssueType) {
      url_ = url_ + '&idIssueType=' + idIssueType;
    }
    return this.http.get<boolean>(url_);
  }
  getCRMStatus(idStatus: number): Observable<CrmStatusDTO> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/{idStatus}/getstatus';
    if (idStatus) {
      url_ = url_.replace('{idStatus}', idStatus.toString());
    }
    return this.http.get<CrmStatusDTO>(url_);
  }
  getCrmIssueDetails(idIssue: number, sourceType: string): Observable<CrmIssueContractDTO> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/crmissue/{idIssue}/getcrmissuedetails';
    if (idIssue) {
      url_ = url_.replace('{idIssue}', idIssue.toString());
    }
    if (sourceType) {
      url_ = url_ + '?sourceType=' + sourceType;
    }
    return this.http.get<CrmIssueContractDTO>(url_);
  }

  getExpenseType(idMember: number): Observable<any> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/claim/member/{idMember}/expensetype';
    url_ = url_.replace('{idMember}', idMember.toString());
    return this.http.get<any>(url_);
  }

  getSubExpenseType(idExpenseType: number): Observable<any> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/claim/expenseType/{idExpenseType}/subexpensetype';
    url_ = url_.replace('{idExpenseType}', idExpenseType.toString());
    return this.http.get<any>(url_);
  }

  getRecurringBenefitMonths(idMember: number, idCRMRecurringClaim?: number, purchasedOn: Date = null): Observable<any> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/recurring/{idMember}/benefitmonths/';
    url_ = url_.replace('{idMember}', idMember.toString());
    if (idCRMRecurringClaim != null && idCRMRecurringClaim != undefined && idCRMRecurringClaim != 0) {
      url_ += idCRMRecurringClaim.toString();
    }
    if (purchasedOn != null) {
      const purchasedOnValue = `${purchasedOn.getFullYear()}-${String(purchasedOn.getMonth() + 1).padStart(2, '0')}-${String(purchasedOn.getDate()).padStart(2, '0')}`;
      url_ += '?purchaseDate=' + encodeURIComponent(purchasedOnValue); 
    }
    return this.http.get<any>(url_);
  }

  getBikePurchaseRecurringClaim(
    idMember: number,
    idCRMRecurringClaim: number
  ): Observable<BikePurchaseRecurringClaimDTO | BikePurchaseRecurringClaimDTO[]> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{Idmember}/{IdCRMRecurringClaim}';
    url = url.replace('{Idmember}', idMember.toString());
    url = url.replace('{IdCRMRecurringClaim}', idCRMRecurringClaim.toString());
    return this.http.get<BikePurchaseRecurringClaimDTO | BikePurchaseRecurringClaimDTO[]>(url);
  }

  cancellationReasons(idMember: number): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{Idmember}/cancellationreasons';
    url = url.replace('{Idmember}', idMember.toString());
    return this.http.get<any>(url);
  }

  cancelEnrolment(idMember: number, idCRMRecurringClaim: number, cancelData: any, benefitMonth: Date | string | null = null): Observable<boolean> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{Idmember}/cancel/{IdCRMRecurringClaim}';
    const benefitMonthValue = benefitMonth instanceof Date
      ? `${benefitMonth.getFullYear()}-${String(benefitMonth.getMonth() + 1).padStart(2, '0')}-${String(benefitMonth.getDate()).padStart(2, '0')}`
      : benefitMonth;

    url = url.replace('{Idmember}', idMember.toString());
    url = url.replace('{IdCRMRecurringClaim}', idCRMRecurringClaim.toString());

    if (benefitMonthValue !== null && benefitMonthValue !== undefined && benefitMonthValue !== '') {
      url += '?benefitMonth=' + encodeURIComponent(benefitMonthValue.toString());
    }

    return this.http.post<boolean>(url, cancelData);
  }

  getGrhClaimReason(): Observable<crmGrhClaimReasonDTO[]> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/claim/grh/claimreason';
    return this.http.get<crmGrhClaimReasonDTO[]>(url_);
  }

  getPreTaxCapConfig(): Observable<PreTaxCapDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/claim/config/pretaxcap';
    return this.http.get<PreTaxCapDTO>(url);
  }
  getGRHSettingsByMember(idMember: number): Observable<TPACompanyCRMSettingDTO> {
    let url = AppConfigService.apiURL(this.apiName) + '/claim/grh/settings/{IdMember}';
    url = url.replace('{IdMember}', idMember.toString());
    return this.http.get<TPACompanyCRMSettingDTO>(url);
  }

  getBalanceHistoryList(idMember: string): Observable<BalanceHistoryDTO[]> {
    let url = AppConfigService.apiURL(this.apiName) + '/claim/{idMember}/parkingclaim/balancehistory';
    if (idMember) {
      url = url.replace('{idMember}', idMember);
    }
    return this.http.get<BalanceHistoryDTO[]>(url);
  }
  deleteClaim(idClaim: number, idClaimType: number): Observable<boolean> {
    let url_ = AppConfigService.apiURL(this.apiName) + '/claim/deletecaimbytype';
    if (idClaim) {
      url_ = url_ + '?idClaim=' + idClaim.toString();
    }
    if (idClaimType) {
      url_ = url_ + '&idClaimtype=' + idClaimType.toString();
    }
    return this.http.post<boolean>(url_, null);
  }

  

  recordCommuteDays(idMember: number, idCRMRecurringClaim: number, commuteDaysData: any): Observable<any> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{IdMember}/commuteday/{IdCRMRecurringClaim}';
    url = url.replace('{IdMember}', idMember.toString());
    url = url.replace('{IdCRMRecurringClaim}', idCRMRecurringClaim.toString());
    return this.http.post<any>(url, commuteDaysData);
  }

  getBenefitMonthCommuteDays(
    idMember: number,
    idCRMRecurringClaim: number,
    benefitMonth: Date | string | null
  ): Observable<CommuteDayDTO[] | RecordCommuteDayDTO[]> {
    let url = AppConfigService.apiURL(this.apiName) + '/recurring/{IdMember}/commuteday/{IdCRMRecurringClaim}';
    const benefitMonthValue = benefitMonth instanceof Date
      ? `${benefitMonth.getFullYear()}-${String(benefitMonth.getMonth() + 1).padStart(2, '0')}-${String(benefitMonth.getDate()).padStart(2, '0')}`
      : benefitMonth;

    url = url.replace('{IdMember}', idMember.toString());
    url = url.replace('{IdCRMRecurringClaim}', idCRMRecurringClaim.toString());

    if (benefitMonthValue !== null && benefitMonthValue !== undefined && benefitMonthValue !== '') {
      url += '?benefitMonth=' + encodeURIComponent(benefitMonthValue.toString());
    }

    return this.http.get<CommuteDayDTO[] | RecordCommuteDayDTO[]>(url);
  }
  
  
  
}
